import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { formatDateTimeInBusinessTZ } from '../../utils/date';

export interface AppEvent {
  id: string;
  event_type: string;
  user_id: string | null;
  user_type: string;
  entity_type: string | null;
  entity_id: string | null;
  session_id: string | null;
  metadata: Record<string, unknown>;
  // `ip_address` is deliberately absent: every writer passes NULL (a browser cannot read its
  // own public IP, and a server-side writer would record Supabase's). The column still
  // exists and is left alone; nothing should present it as data.
  user_agent: string | null;
  referrer: string | null;
  page_url: string | null;
  success: boolean;
  error_message: string | null;
  processing_time_ms: number | null;
  email: string | null;
  created_at: string;
}

interface EventDetailModalProps {
  event: AppEvent;
  /** The Details-column sentence for this row, already resolved against the page's lookup. */
  summary: string;
  badgeClassName: string;
  onClose: () => void;
  onViewSession: (sessionId: string) => void;
}

const Empty = () => <span className="text-gray-400">—</span>;

/**
 * Everything one `app_events` row holds. The Details column is a one-line summary written per
 * event type, so any metadata key a summary doesn't name — and the user agent, referrer and
 * full ids the table truncates or hides — was only reachable through SQL.
 */
export const EventDetailModal: React.FC<EventDetailModalProps> = ({
  event,
  summary,
  badgeClassName,
  onClose,
  onViewSession,
}) => {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const mono = (value: string | null) =>
    value ? <span className="font-mono text-xs break-all">{value}</span> : <Empty />;

  const fields: Array<[string, React.ReactNode]> = [
    ['Time', (
      <>
        {formatDateTimeInBusinessTZ(event.created_at)}
        <div className="font-mono text-xs text-gray-500">{event.created_at}</div>
      </>
    )],
    ['Status', event.success ? 'Succeeded' : <span className="text-red-700">Failed</span>],
    ['Event ID', mono(event.id)],
    ['User', event.email ?? <span className="text-gray-500">Anonymous</span>],
    ['User type', event.user_type || <Empty />],
    ['User ID', mono(event.user_id)],
    ['Entity type', event.entity_type ?? <Empty />],
    ['Entity ID', mono(event.entity_id)],
    ['Session ID', event.session_id ? (
      <>
        {mono(event.session_id)}
        <button
          onClick={() => onViewSession(event.session_id!)}
          className="block text-xs text-blue-600 hover:underline mt-0.5"
        >
          View session
        </button>
      </>
    ) : <Empty />],
    ['Page URL', event.page_url ? <span className="break-all">{event.page_url}</span> : <Empty />],
    ['Referrer', event.referrer ? <span className="break-all">{event.referrer}</span> : <Empty />],
    ['User agent', event.user_agent ? <span className="break-words">{event.user_agent}</span> : <Empty />],
    ['Processing time', event.processing_time_ms != null ? `${event.processing_time_ms} ms` : <Empty />],
  ];

  const hasMetadata = !!event.metadata && Object.keys(event.metadata).length > 0;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex flex-col justify-end sm:items-center sm:justify-center bg-black bg-opacity-40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-detail-title"
        className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-2xl max-h-[85vh] sm:max-h-[80vh] sm:mx-4 flex flex-col"
      >
        <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <h2 id="event-detail-title" className="text-base font-semibold">Event</h2>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${badgeClassName}`}>
              {event.event_type}
            </span>
            {!event.success && (
              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                Failed
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ✕
          </button>
        </div>
        <div className="overflow-y-auto p-4 space-y-4">
          {summary && <p className="text-sm text-gray-700">{summary}</p>}
          {event.error_message && (
            <div className="text-sm text-red-700 bg-red-50 rounded px-3 py-2 break-words whitespace-pre-wrap">
              {event.error_message}
            </div>
          )}
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
            {fields.map(([label, value]) => (
              <React.Fragment key={label}>
                <dt className="text-gray-500">{label}</dt>
                <dd className="text-gray-900 min-w-0">{value}</dd>
              </React.Fragment>
            ))}
          </dl>
          <div>
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Metadata</h3>
            {hasMetadata ? (
              <pre className="text-xs bg-gray-50 border rounded p-3 whitespace-pre-wrap break-words">
                {JSON.stringify(event.metadata, null, 2)}
              </pre>
            ) : (
              <p className="text-sm text-gray-400">No metadata recorded.</p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
